







































































































































































































import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import { AdcontrolProfile } from "@/models";
import { Getter } from "vuex-class";
import WindowEvent from "@/decorators/WindowEvent";
import { Company } from "@/models";
import { Moment } from "moment";
import EntryItem from "@/components/EntryItem.vue";
import beautify from "@/filters/Beautify";

export interface BreakdownAgencyData extends Company {
  total: number;
  values: number[];
  broadcasters: BreakdownBroadcasterData[];
  media: string;
  vod: boolean;
  vodValues: boolean[];
}

export interface BreakdownBroadcasterData {
  name: string;
  total: number;
  logoUrls?: string[];
  values: number[];
  vodValues: boolean[];
  media: string;
  auditel: number;
  vod: boolean;
}

export interface BreakdownDataItem {
  agencies: BreakdownAgencyData[];
  values: number[];
  total: number;
  media: string;
  vod: boolean;
  vodValues: boolean[];
}

export interface BreakdownData {
  items: BreakdownDataItem[];
  values: number[];
  total: number;
  vodValues: boolean[];
  vod: boolean;
}

interface BreakdownTableColumn {
  value: string | Date | Moment;
  label: string;
  isHighlight?: boolean;
}

@Component({
  components: { EntryItem },
})
export default class BroadcasterBreakDownTable extends Vue {
  @Prop() readonly value!: BreakdownData;
  @Prop({ default: false }) readonly loading!: boolean;
  @Prop({ required: true }) readonly granularity!: "day" | "sweek" | "month";
  @Prop({ required: true }) readonly grouped!: boolean;
  @Prop({ required: true }) readonly dateFrom!: string;
  @Prop({ required: true }) readonly dateTo!: string;
  @Prop({ default: 0 }) readonly decimals!: number;

  @Getter("profile") readonly profile!: AdcontrolProfile;
  @Ref() inner!: HTMLDivElement;
  @Ref() table!: HTMLTableElement;

  position = {
    overflow: false,
    scrolledToRight: false,
    scrolledToLeft: true,
  };

  mounted() {
    this.checkPosition();
  }

  @Watch("value")
  onValueChanged() {
    this.checkPosition();
  }

  @WindowEvent("resize")
  onResize() {
    if (!this.inner) {
      return;
    }
    this.checkPosition();
  }

  get hideSumsIfProfileVodOnly(): boolean {
    if (this.profile?.mediaSourceKind == "msk_vod") {
      return true;
    }
    return false;
  }

  get columns(): BreakdownTableColumn[] {
    if (this.loading) {
      return [];
    }
    const start = this.$moment(this.dateFrom);
    const end = this.$moment(this.dateTo);

    let columns: BreakdownTableColumn[] = [];
    const columnsMap: Map<string, BreakdownTableColumn> = new Map();

    switch (this.granularity) {
      case "day":
        for (let date = start; date.isSameOrBefore(end); date.add(1, "days")) {
          const day = date.format("dd");
          const label = day[0].toUpperCase() + " " + date.format("DD/M");

          columns.push({
            value: date,
            label: label,
            isHighlight: date.weekday() == 0,
          });
        }

        break;

      case "sweek":
        for (let date = start; date.isSameOrBefore(end); date.add(1, "days")) {
          const startOfWeek = date.clone().startOf("week");
          const label = startOfWeek.format("D MMM");

          columnsMap.set(label, {
            value: startOfWeek,
            label: label,
            isHighlight: startOfWeek.date() >= 1 && startOfWeek.date() <= 7,
          });
        }

        columns = [...columnsMap.values()];

        break;

      case "month":
        for (let date = start; date.isSameOrBefore(end); date.add(1, "days")) {
          const startOfMonth = date.clone().startOf("month");
          const label = startOfMonth.format("MMM YYYY");

          columnsMap.set(label, {
            value: startOfMonth,
            label: label,
            isHighlight: date.month() == 0,
          });

          columns = [...columnsMap.values()];
        }

        break;
    }
    // Debugging
    // const referenceValue = this.value?.items?.[0]?.agencies?.[0]?.values;
    // const referenceValueLength = referenceValue?.length || 0;
    // if (columns.length != referenceValueLength) {
    //   console.error(`Inconsistent columns length. Expected ${referenceValueLength}, got ${columns.length}`)
    // }

    return columns;
  }

  protected scrollToLeft() {
    this.inner.scrollLeft = 0;
    this.checkPosition();
  }

  protected scrollToRight() {
    this.inner.scrollLeft = this.getTableWidth() - this.getElementWidth();
    this.checkPosition();
  }

  protected checkPosition() {
    this.position.overflow = this.getElementWidth() < this.getTableWidth();
    this.position.scrolledToRight =
      this.inner.scrollLeft == this.getTableWidth() - this.getElementWidth();
    this.position.scrolledToLeft = this.inner.scrollLeft == 0;
  }

  protected getElementWidth(): number {
    return this.inner?.clientWidth || 0;
  }

  protected getTableWidth(): number {
    return this.table?.clientWidth || 0;
  }

  beautifyValue(value: number, decimals: number) {
    return beautify(value, "-", "-", false, decimals);
  }
}
